import { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, Download, Copy, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ReportViewerProps {
  report: string;
  title?: string;
  className?: string;
}

export function ReportViewer({ report, title, className }: ReportViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Render Mermaid diagrams
    if (containerRef.current) {
      renderMermaid(containerRef.current);
    }
  }, [report]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(report);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title?.replace(/\s+/g, '-').toLowerCase() || 'report'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className={cn('bg-card border border-border rounded-2xl overflow-hidden', className)}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-700 dark:text-blue-400" />
          <h3 className="text-sm font-semibold text-foreground">{title || 'Security Report'}</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="text-muted-foreground hover:text-foreground"
          >
            {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-700 dark:text-emerald-400" /> : <Copy className="h-4 w-4" />}
            <span className="ml-1 text-xs">{copied ? 'Copied' : 'Copy'}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownload}
            className="text-muted-foreground hover:text-foreground"
          >
            <Download className="h-4 w-4" />
            <span className="ml-1 text-xs">Download</span>
          </Button>
        </div>
      </div>
      <div
        ref={containerRef}
        className="report-content p-6 max-w-none text-sm text-foreground"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(report) }}
      />
    </Card>
  );
}

// Simple markdown to HTML renderer (no external deps)
function renderMarkdown(md: string): string {
  let html = md;

  // Escape HTML
  html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Code blocks with mermaid support
  html = html.replace(/```mermaid\n([\s\S]*?)```/g, (_, code) => {
    return `<div class="mermaid-container"><pre class="mermaid">${code.trim()}</pre></div>`;
  });

  // Regular code blocks
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
    return `<pre class="report-code-block"><code class="language-${lang}">${code.trim()}</code></pre>`;
  });

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code class="report-inline-code">$1</code>');

  // Headers
  html = html.replace(/^#### (.+)$/gm, '<h4 class="report-h4">$1</h4>');
  html = html.replace(/^### (.+)$/gm, '<h3 class="report-h3">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 class="report-h2">$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1 class="report-h1">$1</h1>');

  // Bold and italic
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground">$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Tables
  html = html.replace(/^\|(.+)\|$/gm, (match) => {
    const cells = match.split('|').filter(c => c.trim() !== '');
    if (cells.every(c => /^[\s-:]+$/.test(c))) {
      return ''; // separator row
    }
    const cellHtml = cells.map(c => `<td>${c.trim()}</td>`).join('');
    return `<tr>${cellHtml}</tr>`;
  });

  // Wrap consecutive table rows
  html = html.replace(/((?:<tr>.*<\/tr>\n?)+)/g, (match) => {
    const rows = match.trim().split('\n').filter(r => r.trim());
    if (rows.length > 0) {
      // First row is header
      const headerRow = rows[0].replace(/<td>/g, '<th>').replace(/<\/td>/g, '</th>');
      const bodyRows = rows.slice(1).join('\n');
      return `<table class="report-table"><thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table>`;
    }
    return match;
  });

  // Lists
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>');
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul class="report-list">$1</ul>');

  // Horizontal rules
  html = html.replace(/^---$/gm, '<hr class="border-border my-4">');

  // Line breaks (two spaces at end) and paragraphs
  html = html.replace(/  $/gm, '<br>');

  // Paragraphs -- wrap loose text
  html = html.replace(/^(?!<[huptlo]|<\/|<br|<hr|<li|<div|<pre|<table|<thead|<tbody|<tr)(.+)$/gm, '<p class="mb-3">$1</p>');

  // Clean up empty paragraphs
  html = html.replace(/<p class="mb-3"><\/p>/g, '');
  html = html.replace(/<p class="mb-3">\s*<\/p>/g, '');

  return html;
}

// Mermaid rendering using CDN
async function renderMermaid(container: HTMLElement) {
  const mermaidElements = container.querySelectorAll('.mermaid');
  if (mermaidElements.length === 0) return;

  // Load mermaid from CDN if not already loaded
  if (!(window as any).mermaid) {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.min.js';
    script.async = true;
    await new Promise<void>((resolve, reject) => {
      script.onload = () => resolve();
      script.onerror = () => reject();
      document.head.appendChild(script);
    });
  }

  const mermaid = (window as any).mermaid;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    themeVariables: {
      darkMode: true,
      background: '#0f172a',
      primaryColor: '#6366f1',
      primaryTextColor: '#e2e8f0',
      lineColor: '#475569',
      secondaryColor: '#1e293b',
      tertiaryColor: '#1e293b',
    },
  });

  for (let i = 0; i < mermaidElements.length; i++) {
    const el = mermaidElements[i] as HTMLElement;
    const code = el.textContent || '';
    try {
      const id = `mermaid-${Date.now()}-${i}`;
      const { svg } = await mermaid.render(id, code);
      const wrapper = document.createElement('div');
      wrapper.className = 'mermaid-rendered my-4 flex justify-center';
      wrapper.innerHTML = svg;
      el.parentElement?.replaceWith(wrapper);
    } catch (err) {
      console.warn('Mermaid render failed:', err);
      // Leave the code block as-is
    }
  }
}
